import { useState, useEffect } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export function loadSettings() {
  try {
    const s = localStorage.getItem("stream_settings");
    if (s) return JSON.parse(s);
  } catch (e) { }
  return {
    apiKey: "",
    baseUrl: "https://api.openai.com/v1",
    model: "gpt-4o-mini",
    autoTag: false
  };
}

export function saveSettings(s) {
  localStorage.setItem("stream_settings", JSON.stringify(s));
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(loadSettings());
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const navigate = useNavigate();

  const handleSave = () => {
    saveSettings(settings);
    navigate("/");
  };

  const applyGeminiPreset = () => {
    setSettings(prev => ({
      ...prev,
      baseUrl: "https://generativelanguage.googleapis.com/v1beta/interactions",
      model: "gemini-3.5-flash"
    }));
  };

  const applyOpenAIPreset = () => {
    setSettings(prev => ({
      ...prev,
      baseUrl: "https://api.openai.com/v1",
      model: "gpt-4o-mini"
    }));
  };

  const testConnection = async () => {
    if (!settings.apiKey) {
      toast.error("Please enter an API key first.");
      return;
    }
    const isGeminiNative = settings.baseUrl.includes("generativelanguage.googleapis.com");

    let url = settings.baseUrl;
    let headers = { "Content-Type": "application/json" };
    let body = {};

    if (isGeminiNative) {
      headers["x-goog-api-key"] = settings.apiKey;
      body = {
        model: settings.model,
        input: "Say the word 'Hello'"
      };
    } else {
      url = `${settings.baseUrl.replace(/\/$/, '')}/chat/completions`;
      headers["Authorization"] = `Bearer ${settings.apiKey}`;
      body = {
        model: settings.model,
        messages: [{ role: "user", content: "Say the word 'Hello'" }],
        max_tokens: 5
      };
    }

    try {
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        let errMsg = await res.text();
        try {
          const parsed = JSON.parse(errMsg);
          if (Array.isArray(parsed) && parsed[0]?.error?.message) {
            errMsg = parsed[0].error.message;
          } else if (parsed.error?.message) {
            errMsg = parsed.error.message;
          }
        } catch (e) {}
        throw new Error(errMsg);
      }
      toast.success("Connection successful!");
    } catch (err) {
      toast.error(`Error: ${err.message}`);
      console.error(err);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6" data-testid="settings-page">
      <div className="max-w-md mx-auto pt-20">
        <button onClick={() => navigate("/")} className="mb-6 flex items-center text-sm meta-label text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Stream
        </button>

        <h1 className="font-serif-journal text-3xl mb-2">Settings</h1>
        <p className="meta-label text-muted-foreground mb-8 text-xs">Configure your AI provider for auto-tagging.</p>

        <div className="flex gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={applyGeminiPreset} className="text-xs h-7 px-3 meta-label">
            Use Gemini 2.5
          </Button>
          <Button variant="outline" size="sm" onClick={applyOpenAIPreset} className="text-xs h-7 px-3 meta-label">
            Use OpenAI
          </Button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="meta-label text-xs mb-2 block text-muted-foreground">AI API Key (BYOK)</label>
            <div className="relative">
              <Input
                type={showApiKey ? "text" : "password"}
                value={settings.apiKey}
                onChange={e => setSettings({ ...settings, apiKey: e.target.value })}
                placeholder="sk-..."
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex="-1"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="meta-label text-xs mb-2 block text-muted-foreground">Base URL</label>
            <Input
              value={settings.baseUrl}
              onChange={e => setSettings({ ...settings, baseUrl: e.target.value })}
            />
          </div>
          <div>
            <label className="meta-label text-xs mb-2 block text-muted-foreground">Model Name</label>
            <Input
              value={settings.model}
              onChange={e => setSettings({ ...settings, model: e.target.value })}
            />
          </div>
          <div className="flex items-start gap-3 mt-8">
            <input
              type="checkbox"
              id="autotag"
              className="mt-1"
              checked={settings.autoTag}
              onChange={e => setSettings({ ...settings, autoTag: e.target.checked })}
            />
            <label htmlFor="autotag" className="text-sm">Automatically generate AI tags when creating new journal entries.</label>
          </div>
        </div>

        <div className="mt-12 flex justify-end gap-3">
          <Button variant="outline" onClick={testConnection} disabled={testing}>
            {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Test Connection
          </Button>
          <Button onClick={handleSave}>Save Settings</Button>
        </div>
      </div>
    </div>
  );
}
